import { useState, useCallback } from "react";
import apiService from "../service/api-service";
import { AppConfig } from "../app-configs/app-config";
import useNotification from "./useNotification";
import { Utills } from "../app-configs/utilit";
import { loginRequest, msalConfig } from "../app-configs/authconfig";
import { PublicClientApplication } from "@azure/msal-browser";
import { useTransactionCertificateStore } from "../../store/transaction-certificates.store";

/**
 * useApiV4 — V4-only variant of useApi.
 *
 * Differences from the shared useApi:
 *  - On a 422 Unprocessable Entity response the hook RETURNS the error payload
 *    (instead of swallowing it and returning undefined).  This lets V4 submit
 *    handlers inspect backend field-level validation errors for cross-tab error
 *    display without touching the shared hook used by V3.
 *
 * All other behaviour (401 silent refresh, 409, 400/500 toasts, etc.) is
 * identical to useApi.
 *
 * V3 code must continue using `useApi` from `./useApi`.
 */

const msalInstance: any = new PublicClientApplication(msalConfig);

type ApiResponse<T> = {
  data: T;
  status: number;
  message?: string;
};

interface UseApiV4Hook<R = any> {
  apiData: R | null;
  loading: boolean;
  error: Error | null;
  setLoadingState: (state: boolean) => void;
  executeGet: (
    url: string,
    params?: Record<string, any>,
    config?: Record<string, any>,
    baseUrlOverride?: string
  ) => Promise<ApiResponse<R> | undefined>;
  executePost: <T>(
    url: string,
    data: T,
    config?: Record<string, any>,
    baseUrlOverride?: string
  ) => Promise<ApiResponse<R> | undefined>;
  executePut: <T>(
    url: string,
    data?: T,
    config?: Record<string, any>,
    baseUrlOverride?: string
  ) => Promise<ApiResponse<R> | undefined>;
  executeDelete: (
    url: string,
    params?: Record<string, any>,
    config?: Record<string, any>,
    baseUrlOverride?: string
  ) => Promise<ApiResponse<R> | undefined>;
}

export function useApiV4<R = any>(
  defaultBaseUrl: string = AppConfig.BASE_URL
): UseApiV4Hook<R> {
  const [apiData, setApiData] = useState<R | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { notify } = useNotification();

  const setLoadingState = useCallback((state: boolean) => {
    setLoading(state);
  }, []);

  const handleRequest = useCallback(
    async (
      method: "get" | "post" | "put" | "delete",
      url: string,
      options: any,
      baseUrlOverride?: string
    ): Promise<ApiResponse<R> | undefined> => {
      setLoading(true);
      setError(null);
      try {
        const baseUrl = baseUrlOverride || defaultBaseUrl;
        if (!baseUrl) {
          throw new Error("Base URL is not defined.");
        }
        const endpoint = `${url}`;
        let response: ApiResponse<R>;

        switch (method) {
          case "get":
            response = await apiService.get<R>(
              endpoint,
              options?.params,
              options?.config
            );
            break;
          case "post":
            response = await apiService.post<any, R>(
              endpoint,
              options?.data,
              options?.config
            );
            break;
          case "put":
            response = await apiService.put<any, R>(
              endpoint,
              options?.data,
              options?.config
            );
            break;
          case "delete":
            response = await apiService.delete<R>(
              endpoint,
              options?.params,
              options?.config
            );
            break;
          default:
            throw new Error(`Unsupported method: ${method}`);
        }
        Utills.cl(`handlereq response ${JSON.stringify(response)}`);
        setApiData(response.data);
        return response;
      } catch (err: any) {
        setError(err as Error);
        if (err.response?.status === 401) {
          await msalInstance.initialize();
          const tokenRequest = {
            ...loginRequest,
            account: msalInstance.getAllAccounts()[0],
          };
          const response: any = await msalInstance
            .acquireTokenSilent(tokenRequest)
            .catch((error: any) => {
              if (error.name === "InteractionRequiredAuthError") {
                msalInstance.loginRedirect(tokenRequest);
              }
            });

          if (response?.idToken) {
            const newToken = response?.idToken;
            useTransactionCertificateStore.getState().setAuthToken(response?.idToken);
            useTransactionCertificateStore.getState().setTokenExpiry(new Date(response.expiresOn).getTime().toString());
            let newConfig = options?.config;
            if (newConfig) {
              newConfig.headers["Authorization"] = newToken;
            }
            handleRequest(method, url, { params: options?.params, config: newConfig }, baseUrlOverride);
          } else {
            localStorage.clear();
            sessionStorage.clear();
            msalInstance.logoutRedirect();
          }
        } else if (err.response?.status === 409) {
          const errMsg = err.response?.data?.Failed || "Number already exists";
          notify(errMsg, "error");
          return {
            data: {} as R,
            message: err.response?.data?.Failed,
            status: 409,
          };
        } else if (err.response?.status === 422) {
          // Show toast notifications for the 422 errors (same as useApi)
          if (
            err.response?.data?.errors &&
            err.response?.data?.errors?.length > 0
          ) {
            err.response?.data?.errors.map((error: string) => {
              notify(error, "error");
            });
          } else if (
            err.response?.data?.detail &&
            Array.isArray(err.response?.data?.detail)
          ) {
            err.response?.data?.detail.forEach((errorObj: any) => {
              const field = errorObj.loc ? errorObj.loc[errorObj.loc.length - 1] : "Field";
              const message = errorObj.msg || "Validation error";
              notify(`${field}: ${message}`, "error");
            });
          } else {
            const errMsg = err.response?.data?.error || "Validation Failed";
            notify(errMsg, "error");
          }

          // V4-ONLY: Return the 422 payload so callers can do cross-tab error display.
          // This is the key difference from useApi which returns undefined on 422.
          return {
            data: err.response?.data as R,
            message: "Validation Failed",
            status: 422,
          };
        } else if (err.response?.status === 400 || err.response?.status === 500) {
          const errMsg = err.response?.data?.message || err.response?.data?.error || "Bad Request";
          notify(errMsg, "error");
          return {
            data: err.response?.data as R,
            message: errMsg,
            status: 400,
          };
        } else {
          notify("API Error", "error");
        }
      } finally {
        setLoading(false);
      }
    },
    [defaultBaseUrl, notify]
  );

  const executeGet = useCallback(
    async (
      url: string,
      params?: Record<string, any>,
      config?: Record<string, any>,
      baseUrlOverride?: string
    ): Promise<ApiResponse<R> | undefined> =>
      handleRequest("get", url, { params, config }, baseUrlOverride),
    [handleRequest]
  );

  const executePost = useCallback(
    async <T>(
      url: string,
      data: T,
      config?: Record<string, any>,
      baseUrlOverride?: string
    ): Promise<ApiResponse<R> | undefined> =>
      handleRequest("post", url, { data, config }, baseUrlOverride),
    [handleRequest]
  );

  const executePut = useCallback(
    async <T>(
      url: string,
      data?: T,
      config?: Record<string, any>,
      baseUrlOverride?: string
    ): Promise<ApiResponse<R> | undefined> =>
      handleRequest("put", url, { data, config }, baseUrlOverride),
    [handleRequest]
  );

  const executeDelete = useCallback(
    async (
      url: string,
      params?: Record<string, any>,
      config?: Record<string, any>,
      baseUrlOverride?: string
    ): Promise<ApiResponse<R> | undefined> =>
      handleRequest("delete", url, { params, config }, baseUrlOverride),
    [handleRequest]
  );

  return {
    apiData,
    loading,
    error,
    setLoadingState,
    executeGet,
    executePost,
    executePut,
    executeDelete,
  };
}
